/* REFURB AVENUE — native Shopify commerce, enhanced progressively. */
(() => {
  'use strict';
  if (window.RACommerce) { window.RACommerce.init(document); return; }
  const root = () => window.Shopify?.routes?.root || '/';
  const productRequests = new WeakMap();
  const productObservers = new WeakMap();
  const adding = new WeakSet();
  let revision = 0;
  let mutationCount = 0;
  let queue = Promise.resolve();
  let cartRenderToken = 0;

  function message(node, text, error = false) {
    if (!node) return;
    node.textContent = text;
    node.hidden = !text;
    node.classList.toggle('is-error', error);
  }
  function validCart(cart) { return cart && Array.isArray(cart.items) && Number.isInteger(cart.item_count); }
  function publishCart(cart, open = false) {
    if (!validCart(cart)) return;
    document.dispatchEvent(new CustomEvent('ra:cart-updated', { detail: { cart, open } }));
  }
  async function responseJSON(response) {
    let result;
    try { result = await response.json(); } catch (_) { throw new Error('La réponse de la boutique est indisponible. Réessayez dans un instant.'); }
    if (!response.ok || result.status >= 400) {
      const explanation = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Cette modification n’a pas pu être enregistrée.';
      throw new Error(explanation);
    }
    return result;
  }

  async function refreshCart({ open = false } = {}) {
    const token = revision;
    try {
      const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      const cart = await responseJSON(response);
      if (token !== revision || mutationCount || !validCart(cart)) return null;
      publishCart(cart, open);
      return cart;
    } catch (_) { return null; }
  }

  function pageMatchesCart(page, cart) {
    if (Number(page.dataset.itemCount) !== cart.item_count || Number(page.dataset.totalPrice) !== cart.total_price) return false;
    const rendered = [...page.querySelectorAll('[data-ra-cart-line]')].map(line => [line.dataset.key, Number(line.dataset.quantity)]);
    return JSON.stringify(rendered) === JSON.stringify(cart.items.map(line => [String(line.key), Number(line.quantity)]));
  }
  async function renderCartPage(cart) {
    const page = document.querySelector('[data-ra-cart]');
    if (!page || pageMatchesCart(page, cart)) return;
    const token = ++cartRenderToken;
    const note = page.querySelector('[name="note"]')?.value;
    const url = new URL(`${root()}cart`, window.location.origin);
    url.searchParams.set('section_id', page.dataset.sectionId);
    const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Le panier ne peut pas être actualisé pour le moment.');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const replacement = html.querySelector('[data-ra-cart]');
    if (!replacement || token !== cartRenderToken || !page.isConnected) return;
    if (note !== undefined && replacement.querySelector('[name="note"]')) replacement.querySelector('[name="note"]').value = note;
    page.replaceWith(replacement);
  }

  function changeLine(key, quantity, { open = false } = {}) {
    const operation = async () => {
      if (!key || !Number.isInteger(quantity) || quantity < 0) throw new Error('La quantité doit être un nombre entier positif ou nul.');
      revision += 1;
      mutationCount += 1;
      try {
        const snapshot = await responseJSON(await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }));
        const selected = snapshot.items.find(line => line.key === key);
        if (!selected) throw new Error('Cet article a changé. Actualisez votre panier.');
        const group = selected.properties?._ra_warranty_group;
        const role = selected.properties?._ra_warranty_role;
        let cart;
        if (group && role === 'warranty' && quantity === 0) {
          cart = await responseJSON(await fetch(`${root()}cart/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) }));
          const parent = cart.items.find(line => line.properties?._ra_warranty_group === group && line.properties?._ra_warranty_role === 'product');
          if (parent) {
            const properties = { ...parent.properties, 'Garantie commerciale': '6 mois incluse' };
            ['_ra_warranty_group', '_ra_warranty_role', '_ra_warranty_variant'].forEach(name => delete properties[name]);
            cart = await responseJSON(await fetch(`${root()}cart/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: parent.key, quantity: parent.quantity, properties }) }));
          }
        } else if (group) {
          const updates = {};
          snapshot.items.filter(line => line.properties?._ra_warranty_group === group).forEach(line => { updates[line.key] = quantity; });
          cart = await responseJSON(await fetch(`${root()}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) }));
        } else {
          cart = await responseJSON(await fetch(`${root()}cart/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) }));
        }
        if (!validCart(cart)) throw new Error('Le panier reçu est incomplet.');
        publishCart(cart, open);
        await renderCartPage(cart);
        return cart;
      } finally { mutationCount = Math.max(0, mutationCount - 1); }
    };
    const pending = queue.then(operation, operation);
    queue = pending.catch(() => {});
    return pending;
  }

  async function changeVariant(input) {
    const product = input.closest('[data-ra-product]');
    if (!product) return;
    const selectedInput = input.matches('select') ? input.selectedOptions[0] : input;
    const chosen = [...product.querySelectorAll('[data-ra-option-radio]:checked, [data-ra-option-select] option:checked')];
    const ids = chosen.map(option => option.dataset.optionValueId).filter(Boolean);
    const destination = selectedInput.dataset.productUrl || product.dataset.productUrl;
    const url = new URL(destination, window.location.origin);
    if (url.origin !== window.location.origin) return;
    url.searchParams.delete('variant');
    if (ids.length) url.searchParams.set('option_values', ids.join(','));
    const navigationUrl = new URL(url);
    url.searchParams.set('section_id', product.dataset.sectionId);
    const previous = productRequests.get(product);
    previous?.abort();
    const controller = new AbortController();
    productRequests.set(product, controller);
    product.setAttribute('aria-busy', 'true');
    product.querySelectorAll('[name="add"], [name="id"]').forEach(button => { button.disabled = true; });
    const focusId = input.id;
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal });
      if (!response.ok) throw new Error('Impossible de charger cette configuration.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-ra-product]');
      if (!replacement || controller.signal.aborted || !product.isConnected) return;
      const warrantySelection = captureWarranty(product);
      const quantityInput = replacement.querySelector('[name="quantity"]');
      if (quantityInput) quantityInput.value = product.querySelector('[name="quantity"]')?.value || '1';
      productObservers.get(product)?.disconnect();
      product.replaceWith(replacement);
      restoreWarranty(replacement, warrantySelection);
      if (replacement.dataset.selectedVariant) {
        navigationUrl.searchParams.delete('option_values');
        navigationUrl.searchParams.set('variant', replacement.dataset.selectedVariant);
      }
      window.history.replaceState({ ...window.history.state }, '', navigationUrl.pathname + navigationUrl.search + navigationUrl.hash);
      const updatedInput = document.getElementById(focusId);
      updatedInput?.focus({ preventScroll: true });
      initialize(replacement);
      window.Shopify?.PaymentButton?.init?.();
      replacement.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: replacement.dataset.sectionId } }));
      document.dispatchEvent(new CustomEvent('ra:variant-changed', { detail: { variantId: replacement.dataset.selectedVariant, productId: replacement.dataset.productId, productUrl: replacement.dataset.productUrl } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A native reload resolves the same option IDs without ever buying the old variant.
      window.location.assign(navigationUrl.href);
    }
  }

  function changeMedia(gallery, button) {
    const template = gallery.querySelector(`template[data-ra-media-template="${CSS.escape(button.dataset.mediaId)}"]`);
    const stage = gallery.querySelector('[data-ra-media-stage]');
    if (!template || !stage) return;
    stage.querySelectorAll('video').forEach(video => video.pause());
    stage.replaceChildren(template.content.cloneNode(true));
    gallery.querySelectorAll('[data-ra-thumb]').forEach(thumb => {
      const selected = thumb === button;
      thumb.classList.toggle('is-selected', selected);
      thumb.setAttribute('aria-pressed', String(selected));
    });
    const counter = gallery.querySelector('[data-ra-gallery-counter]');
    if (counter) counter.textContent = `${button.dataset.mediaIndex} / ${gallery.querySelectorAll('[data-ra-thumb]').length}`;
    const zoom = gallery.querySelector('[data-ra-zoom]');
    if (zoom) zoom.hidden = template.dataset.mediaType !== 'image';
  }

  function zoomImage(button) {
    const image = button.closest('[data-ra-gallery]')?.querySelector('[data-ra-media-stage] img');
    if (!image) return;
    if (typeof HTMLDialogElement === 'undefined') { window.open(image.currentSrc || image.src, '_blank', 'noopener'); return; }
    const dialog = document.createElement('dialog');
    dialog.className = 'ra-image-dialog';
    dialog.setAttribute('aria-label', 'Vue agrandie du produit');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ra-image-dialog__close';
    close.setAttribute('aria-label', 'Fermer l’image');
    close.textContent = '×';
    const full = document.createElement('img');
    full.src = image.src;
    full.alt = image.alt;
    dialog.append(close, full);
    document.body.append(dialog);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => { document.body.style.overflow = overflow; dialog.remove(); button.focus({ preventScroll: true }); }, { once: true });
    dialog.showModal();
    close.focus();
  }

  function captureWarranty(product) {
    return { value: product.querySelector('[data-ra-warranty-choice]:checked')?.value || 'included', consent: !!product.querySelector('[data-ra-warranty-consent]')?.checked };
  }
  function restoreWarranty(product, state) {
    const choices = [...product.querySelectorAll('[data-ra-warranty-choice]')];
    const selected = choices.find(choice => choice.value === state.value) || choices.find(choice => choice.value === 'included');
    if (selected) selected.checked = true;
    const consent = product.querySelector('[data-ra-warranty-consent]');
    if (consent) consent.checked = !!state.consent && selected?.value === state.value;
    syncWarranty(product);
  }
  function syncSelectionPrice(product, selected) {
    const rawBase = product.dataset.variantPrice;
    if (rawBase === undefined || rawBase === '') return;
    const basePrice = Number(rawBase);
    if (!Number.isSafeInteger(basePrice) || basePrice < 0) return;
    const paid = !!selected && selected.value !== 'included';
    const fee = paid ? Number(selected.dataset.price) : 0;
    if (!Number.isSafeInteger(fee) || fee < 0) return;
    const unitPrice = basePrice + fee;
    const rawQuantity = Number(product.querySelector('[name="quantity"]')?.value || 1);
    const quantity = Number.isInteger(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;
    const money = value => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: product.dataset.currency || 'EUR' }).format(value / 100);
    product.querySelectorAll('[data-ra-selection-unit-price]').forEach(node => { node.textContent = money(unitPrice); });
    // The product's reference price applies to the device, not to the optional service.
    product.querySelectorAll('[data-ra-device-promotion]').forEach(node => { node.hidden = paid; });
    const breakdown = product.querySelector('[data-ra-selection-breakdown]');
    if (breakdown) {
      breakdown.hidden = !paid;
      breakdown.textContent = paid ? `Appareil : ${money(basePrice)} + garantie ${selected.dataset.months} mois supplémentaires : ${money(fee)}. Prix par appareil, option comprise.` : '';
    }
    const summary = product.querySelector('[data-ra-selection-total]');
    if (summary) {
      summary.hidden = !paid;
      const title = summary.querySelector('[data-ra-selection-total-label]');
      const amount = summary.querySelector('[data-ra-selection-total-price]');
      const detail = summary.querySelector('[data-ra-selection-total-detail]');
      if (title) title.textContent = quantity > 1 ? `Total pour ${quantity} appareils` : 'Total appareil + garantie';
      if (amount) amount.textContent = money(unitPrice * quantity);
      if (detail) detail.textContent = paid ? `${money(basePrice)} + ${money(fee)} par appareil${quantity > 1 ? `, × ${quantity}` : ''}. Garantie ${selected.dataset.months} mois supplémentaires.` : '';
    }
  }
  function syncWarranty(product) {
    const wrapper = product.querySelector('[data-ra-warranty-choices]');
    if (!wrapper) { syncSelectionPrice(product, null); return; }
    wrapper.hidden = false;
    const selected = wrapper.querySelector('[data-ra-warranty-choice]:checked');
    const paid = selected && selected.value !== 'included';
    syncSelectionPrice(product, selected);
    const consentWrap = wrapper.querySelector('[data-ra-warranty-consent-wrap]');
    const consent = wrapper.querySelector('[data-ra-warranty-consent]');
    if (consentWrap) consentWrap.hidden = !paid;
    if (consent) consent.required = !!paid;
    const quantity = Math.max(1, Number(product.querySelector('[name="quantity"]')?.value) || 1);
    const cost = wrapper.querySelector('[data-ra-warranty-cost]');
    if (cost) cost.textContent = paid ? `Option : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(selected.dataset.price) * quantity / 100)} pour ${quantity} appareil${quantity > 1 ? 's' : ''}.` : '';
    // If JS stops after selecting a paid option, native submission must fail rather
    // than silently purchase only the appliance. AJAX explicitly supplies both IDs.
    const id = product.querySelector('[data-ra-add-form] [name="id"]');
    if (id) id.disabled = !!paid || product.dataset.variantAvailable !== 'true' || product.getAttribute('aria-busy') === 'true';
  }
  function warrantyContractDocument(content) {
    if (!content?.dataset.guarantorPhone?.trim() || !content.dataset.contractVersion) throw new Error('Les conditions de garantie ne sont pas disponibles au téléchargement.');
    const copy = content.cloneNode(true);
    copy.querySelectorAll('script, iframe, object, embed, form, input, button, link, meta, base').forEach(node => node.remove());
    copy.querySelectorAll('*').forEach(node => {
      [...node.attributes].forEach(attribute => {
        if (/^on/i.test(attribute.name) || ['srcdoc', 'contenteditable'].includes(attribute.name)) node.removeAttribute(attribute.name);
        if (['href', 'src', 'action'].includes(attribute.name) && /^\s*(?:javascript|data|vbscript):/i.test(attribute.value)) node.removeAttribute(attribute.name);
      });
    });
    return '<!doctype html><html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'"><title>REFURB AVENUE — Contrat de garantie commerciale</title><style>body{font:16px/1.65 system-ui,sans-serif;color:#16243b;max-width:900px;margin:40px auto;padding:0 24px}h1,h2,h3{line-height:1.25}h1{font-size:30px}h2{font-size:24px}h3{font-size:19px;margin-top:30px}table{width:100%;border-collapse:collapse}td,th{padding:10px;border:1px solid #d9e1ee;text-align:left}a{color:#0755ed}p{overflow-wrap:anywhere}@media print{body{font-size:10pt;margin:0;max-width:none}h2,h3{break-after:avoid}tr{break-inside:avoid}}</style></head><body><h1>REFURB AVENUE</h1>' + copy.outerHTML + '</body></html>';
  }
  async function downloadWarrantyContract(link) {
    if (link.dataset.downloading === 'true') return false;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    link.dataset.downloading = 'true';
    link.setAttribute('aria-busy', 'true');
    try {
      let content = document.querySelector('[data-ra-contract-content]');
      if (!content) {
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Conditions indisponibles.');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        content = page.querySelector('[data-ra-contract-content]');
      }
      const html = warrantyContractDocument(content);
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const download = document.createElement('a');
      download.href = blobUrl;
      download.download = 'refurb-avenue-garantie-commerciale-' + content.dataset.contractVersion.replace(/[^0-9-]/g, '') + '.html';
      download.hidden = true;
      document.body.appendChild(download);
      download.click();
      download.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return true;
    } catch (_) {
      window.location.assign(url.href);
      return false;
    } finally {
      delete link.dataset.downloading;
      link.removeAttribute('aria-busy');
    }
  }
  function buildWarrantyItems({ id, quantity, properties = {}, sellingPlan, choice, productTitle, sku, group, termsUrl, startText, contractPhone, contractVersion }) {
    if (!id || !Number.isInteger(quantity) || quantity < 1) throw new Error('Sélectionnez une quantité valide.');
    const item = { id: String(id), quantity, properties: { ...properties } };
    if (sellingPlan) item.selling_plan = sellingPlan;
    if (!choice || choice.value === 'included') return [item];
    const months = Number(choice.months), price = Number(choice.price);
    if (!choice.value || !((months === 12 && price === 5000) || (months === 24 && price === 10000))) throw new Error('Cette garantie doit être actualisée avant la commande.');
    const duration = `${months} mois supplémentaires (${months + 6} mois au total)`;
    item.properties = { ...item.properties, 'Garantie commerciale': duration, _ra_warranty_group: group, _ra_warranty_role: 'product', _ra_warranty_variant: String(choice.value) };
    const warranty = { id: String(choice.value), quantity, properties: { 'Produit couvert': productTitle, 'Référence couverte': sku || String(id), 'Durée de l’extension': `${months} mois`, 'Couverture totale': `${months + 6} mois depuis la livraison`, 'Conditions': termsUrl || '', 'Prise d’effet': startText || '', _ra_parent_variant: String(id), _ra_warranty_group: group, _ra_warranty_role: 'warranty' } };
    if (contractPhone) warranty.properties['Téléphone du garant'] = contractPhone;
    if (contractVersion) warranty.properties['Version du contrat'] = contractVersion;
    return [item, warranty];
  }

  async function addProduct(form) {
    if (adding.has(form) || !form.reportValidity()) return;
    const variant = form.querySelector('[name="id"]');
    if (!variant?.value) return;
    const product = form.closest('[data-ra-product]');
    const status = form.querySelector('[data-ra-product-message]');
    const controls = product ? [...product.querySelectorAll('[name="add"]')] : [...form.querySelectorAll('[type="submit"]')];
    if (product?.dataset.variantAvailable !== 'true' || product.getAttribute('aria-busy') === 'true') return;
    const selectedWarranty = product.querySelector('[data-ra-warranty-choice]:checked');
    const paidWarranty = selectedWarranty && selectedWarranty.value !== 'included';
    const body = new FormData(form);
    adding.add(form);
    controls.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    message(status, 'Ajout en cours…');
    revision += 1;
    mutationCount += 1;
    let succeeded = false;
    try {
      let requestBody = body;
      let headers = { Accept: 'application/json' };
      if (paidWarranty) {
        const warrantyBlock = product.querySelector('[data-ra-warranty]');
        if (!warrantyBlock?.dataset.guarantorPhone?.trim()) throw new Error('Cette option de garantie n’est pas disponible actuellement.');
        const warrantyUrl = new URL(warrantyBlock.dataset.productUrl + '.js', window.location.origin);
        const warrantyProduct = await responseJSON(await fetch(warrantyUrl.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }));
        const actual = warrantyProduct.variants?.find(item => String(item.id) === selectedWarranty.value);
        if (!actual?.available || Number(actual.price) !== Number(selectedWarranty.dataset.price)) throw new Error('L’option de garantie a changé. Actualisez la page pour vérifier son tarif.');
        const properties = {};
        for (const [name, value] of body.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
          if (typeof value !== 'string' && value.size) throw new Error('Un fichier personnalisé ne peut pas être associé à cette option. Contactez-nous avant de commander.');
        }
        const group = window.crypto?.randomUUID?.() || `ra-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        requestBody = JSON.stringify({ items: buildWarrantyItems({ id: variant.value, quantity: Number(body.get('quantity') || 1), properties, sellingPlan: body.get('selling_plan'), choice: { value: selectedWarranty.value, months: selectedWarranty.dataset.months, price: actual.price }, productTitle: product.dataset.productTitle, sku: product.dataset.variantSku, group, termsUrl: new URL(warrantyBlock.dataset.termsUrl, window.location.origin).href, startText: warrantyBlock.dataset.startText, contractPhone: warrantyBlock.dataset.guarantorPhone, contractVersion: warrantyBlock.dataset.contractVersion || '' }) });
        headers['Content-Type'] = 'application/json';
      }
      const response = await fetch(`${root()}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers, body: requestBody });
      await responseJSON(response);
      succeeded = true;
      message(status, 'Ajouté au panier.');
    } catch (error) { message(status, error.message, true); }
    finally {
      mutationCount = Math.max(0, mutationCount - 1);
      adding.delete(form);
      controls.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
    }
    const cart = await refreshCart({ open: succeeded });
    if (succeeded && !cart) window.location.assign(`${root()}cart`);
  }

  async function loadRecommendations(section) {
    if (!section.dataset.productId || section.dataset.loaded === section.dataset.productId) return;
    const productId = section.dataset.productId;
    section.dataset.loaded = productId;
    const url = new URL(`${root()}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', section.dataset.sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', section.dataset.limit || '8');
    url.searchParams.set('intent', 'related');
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-ra-recommendations]');
      if (section.dataset.productId !== productId || !replacement) return;
      const cards = replacement.querySelector('.ra-recommendations__grid');
      if (cards?.children.length) { section.innerHTML = replacement.innerHTML; section.hidden = false; }
    } catch (_) { /* Keep any server-rendered collection selection on temporary failure. */ }
  }

  function initialize(scope = document) {
    const products = [...scope.querySelectorAll('[data-ra-product]')];
    if (scope.matches?.('[data-ra-product]')) products.unshift(scope);
    products.forEach(product => {
      syncWarranty(product);
      if (productObservers.has(product)) return;
      const form = product.querySelector('[data-ra-add-form]');
      const sticky = product.querySelector('[data-ra-sticky-buy]');
      if (form && sticky && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0)), { threshold: 0 });
        observer.observe(form);
        productObservers.set(product, observer);
      }
    });
    const recommendations = [...scope.querySelectorAll('[data-ra-recommendations]')];
    if (scope.matches?.('[data-ra-recommendations]')) recommendations.unshift(scope);
    recommendations.forEach(loadRecommendations);
  }

  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-ra-warranty-choice], [name="quantity"]') && input.closest('[data-ra-product]')) syncWarranty(input.closest('[data-ra-product]'));
    if (input.matches('[data-ra-option-radio], [data-ra-option-select]')) { changeVariant(input); return; }
    if (input.matches('[data-ra-cart-quantity-input]')) {
      const page = input.closest('[data-ra-cart]');
      if (page.getAttribute('aria-busy') === 'true') return;
      const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 0) { input.value = input.defaultValue; return; }
      page.setAttribute('aria-busy', 'true');
      changeLine(input.closest('[data-ra-cart-line]').dataset.key, quantity).catch(error => {
        message(document.querySelector('[data-ra-cart-message]'), error.message, true);
        input.value = input.defaultValue;
      }).finally(() => { page.removeAttribute('aria-busy'); });
    }
  });
  document.addEventListener('submit', event => {
    if (event.target.matches('#RaCartForm') && (mutationCount || event.target.closest('[data-ra-cart]')?.getAttribute('aria-busy') === 'true')) {
      event.preventDefault();
      message(document.querySelector('[data-ra-cart-message]'), 'Le panier se met à jour. Patientez avant de continuer.', true);
      return;
    }
    if (!event.target.matches('[data-ra-add-form]')) return;
    event.preventDefault();
    addProduct(event.target);
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('button, a');
    if (!button) return;
    if (button.matches('[data-ra-contract-download]')) { event.preventDefault(); downloadWarrantyContract(button); return; }
    if (button.matches('[data-ra-thumb]')) { changeMedia(button.closest('[data-ra-gallery]'), button); return; }
    if (button.matches('[data-ra-zoom]')) { zoomImage(button); return; }
    if (button.matches('[data-ra-quantity]')) {
      const input = button.closest('.ra-quantity').querySelector('input');
      input.value = String(Math.max(1, (Number(input.value) || 1) + Number(button.dataset.raQuantity)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (button.matches('[data-ra-cart-quantity]')) {
      const input = button.closest('.ra-quantity').querySelector('input');
      if (button.closest('[data-ra-cart]').getAttribute('aria-busy') === 'true') return;
      input.value = String(Math.max(0, (Number(input.value) || 0) + Number(button.dataset.raCartQuantity)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (button.matches('[data-ra-cart-remove]')) {
      event.preventDefault();
      const input = button.closest('[data-ra-cart-line]').querySelector('[data-ra-cart-quantity-input]');
      input.value = '0';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  document.addEventListener('keydown', event => {
    const thumb = event.target.closest('[data-ra-thumb]');
    if (!thumb || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const gallery = thumb.closest('[data-ra-gallery]');
    const thumbs = [...gallery.querySelectorAll('[data-ra-thumb]')];
    let index = thumbs.indexOf(thumb) + (event.key === 'ArrowLeft' ? -1 : 1);
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = thumbs.length - 1;
    const target = thumbs[(index + thumbs.length) % thumbs.length];
    target.focus();
    changeMedia(gallery, target);
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('ra:variant-changed', event => {
    document.querySelectorAll('[data-ra-recommendations]').forEach(section => { section.dataset.productId = event.detail.productId; loadRecommendations(section); });
  });
  window.addEventListener('pageshow', async () => {
    // BFCache is reconciled against Shopify. No local storage cart, clearing or replay.
    const cart = await refreshCart();
    if (cart) { try { await renderCartPage(cart); } catch (_) { /* Native cart stays usable. */ } }
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshCart(); });
  window.RACommerce = { init: initialize, refreshCart, changeLine, buildWarrantyItems };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
})();
